import { SchemaDeSerBuilder, type DeSer } from "./deser"
import { JsonBuilder, type JsonHelper, type JsonMap } from "./util/json"

class PreferencesAccount {
  currentAccount = ""
  privacy = false
  excludedAccounts: string[] = []
}

export class RecentSymbolSearchResult {
  symbol: string | null = null
  description: string | null = null
  listedMarket: string | null = null
  hasOptions: boolean | null = false
  etf = false
  instrumentType: string | null = null
  eventProductExternalId: string | null = null
  externalId: string | null = null
  category: string | null = null
}

const DEFAULT_RECENT_SYMBOLS = ["SPY", "QQQ", "IWM", "DIA", "TLT"]
const DEFAULT_INACTIVITY_TIMEOUT = 120

export function createDefaultRecentSymbolSearchResults(): RecentSymbolSearchResult[] {
  return DEFAULT_RECENT_SYMBOLS.map((symbol) => {
    const result = new RecentSymbolSearchResult()

    result.symbol = symbol
    result.description = symbol
    result.hasOptions = true
    result.etf = true

    return result
  })
}

export class CommonAppsPreferences {
  recentSymbolSearchResultsV2: RecentSymbolSearchResult[] =
    createDefaultRecentSymbolSearchResults()
}

class WebBasedTwoPreferences {
  inactivityTimeout = DEFAULT_INACTIVITY_TIMEOUT
}

export class FollowPreferences {
  hiddenTraderIds: number[] = []
  // Stored as null for all orders; read back as "".
  typeFilter = ""
}

export class Preferences {
  account = new PreferencesAccount()
  commonApps = new CommonAppsPreferences()
  webBasedTwo = new WebBasedTwoPreferences()
  follow = new FollowPreferences()
}

const PREFERENCES_ACCOUNT_DESER = new SchemaDeSerBuilder(PreferencesAccount)
  .ofString("currentAccount")
  .ofBoolean("privacy")
  .ofPrimitiveArray("excludedAccounts")
  .toDeSer()

const WEB_BASED_TWO_PREFERENCES_DESER = new SchemaDeSerBuilder(
  WebBasedTwoPreferences
)
  .ofInt("inactivityTimeout")
  .toDeSer()

const RECENT_SYMBOL_SEARCH_RESULT_DESER = new SchemaDeSerBuilder(
  RecentSymbolSearchResult
)
  .ofNullableString("symbol")
  .ofNullableString("description")
  .ofNullableString("listedMarket")
  .ofBoolean("hasOptions", "options")
  .ofBoolean("etf")
  .ofNullableString("instrumentType")
  .ofNullableString("eventProductExternalId")
  .ofNullableString("externalId")
  .ofNullableString("category")
  .toDeSer()

const COMMON_APPS_PREFERENCES_DESER = new SchemaDeSerBuilder(
  CommonAppsPreferences
)
  .ofArray(
    "recentSymbolSearchResultsV2",
    RECENT_SYMBOL_SEARCH_RESULT_DESER,
    RecentSymbolSearchResult
  )
  .toDeSer()

const HIDDEN_TRADER_IDS = "hidden-trader-ids"
const TYPE_FILTER = "type-filter"

// The J2CL platform serializes each id as a boxed java.lang.Integer: a
// one-field object such as `{"v$jI": 149972}` whose field name is minified
// differently per bundle build, so match the shape, not the name.
// getPrimitiveArray discards the whole array when it holds any object.
const HIDDEN_TRADER_IDS_FIELD: DeSer<FollowPreferences> = {
  update(target: FollowPreferences, helper: JsonHelper): void {
    const value = (helper.toJson() as JsonMap)[HIDDEN_TRADER_IDS]
    if (!Array.isArray(value)) {
      return
    }

    target.hiddenTraderIds = value
      .map((element) => {
        if (element === null || typeof element !== "object") {
          return element
        }
        const fields = Object.values(element)
        return fields.length === 1 ? fields[0] : undefined
      })
      .filter((id): id is number => Number.isInteger(id))
  },
  serialize(target: FollowPreferences, json: JsonBuilder): void {
    json.add(HIDDEN_TRADER_IDS, target.hiddenTraderIds)
  },
}

const FOLLOW_PREFERENCES_DESER = new SchemaDeSerBuilder(FollowPreferences)
  .add(HIDDEN_TRADER_IDS_FIELD)
  .ofString("typeFilter", TYPE_FILTER)
  .toDeSer()

export const PREFERENCES_DESER = new SchemaDeSerBuilder(Preferences)
  .ofNested("account", PREFERENCES_ACCOUNT_DESER, PreferencesAccount)
  .ofNested("commonApps", COMMON_APPS_PREFERENCES_DESER, CommonAppsPreferences)
  .ofNested(
    "webBasedTwo",
    WEB_BASED_TWO_PREFERENCES_DESER,
    WebBasedTwoPreferences
  )
  .ofNested("follow", FOLLOW_PREFERENCES_DESER, FollowPreferences)
  .toDeSer()

export interface CommonAppsPatch extends JsonMap {
  "common-apps": {
    "recent-symbol-search-results-v2": JsonMap[]
  }
}

export function toCommonAppsPatch(
  recentSymbolSearchResults: RecentSymbolSearchResult[]
): CommonAppsPatch {
  const commonApps = new CommonAppsPreferences()
  commonApps.recentSymbolSearchResultsV2 = recentSymbolSearchResults

  const builder = new JsonBuilder()
  COMMON_APPS_PREFERENCES_DESER.serialize(commonApps, builder)

  return { "common-apps": builder.json } as CommonAppsPatch
}

export interface FollowPatch extends JsonMap {
  follow: {
    "hidden-trader-ids"?: number[]
    "type-filter"?: string | null
  }
}

// Only the given fields are written, so a filter change in one tab doesn't
// overwrite hidden traders changed in another.
export function toFollowPatch(changes: {
  hiddenTraderIds?: number[]
  typeFilter?: string | null
}): FollowPatch {
  const follow: FollowPatch["follow"] = {}
  if (changes.hiddenTraderIds !== undefined) {
    follow[HIDDEN_TRADER_IDS] = changes.hiddenTraderIds
  }
  if (changes.typeFilter !== undefined) {
    follow[TYPE_FILTER] = changes.typeFilter
  }

  return { follow }
}

export interface AccountPatch extends JsonMap {
  account: {
    "current-account": string
  }
}

// Only the current account is written, so the other account fields the
// J2CL platform keeps in this namespace (privacy, excluded accounts, ...)
// are left alone.
export function toAccountPatch(currentAccountExternalId: string): AccountPatch {
  return { account: { "current-account": currentAccountExternalId } }
}
